import dayjs from "dayjs";
import numbro from "numbro";
import deDE from "numbro/languages/de-DE.js";
import esES from "numbro/languages/es-ES.js";
import frFR from "numbro/languages/fr-FR.js";
import itIT from "numbro/languages/it-IT.js";
import nlNL from "numbro/languages/nl-NL.js";

import type { Currency, FormatFn, RawDocumentValue, RawValue } from "./types";

numbro.registerLanguage(itIT as numbro.NumbroLanguage);
numbro.registerLanguage(deDE as numbro.NumbroLanguage);
numbro.registerLanguage(esES as numbro.NumbroLanguage);
numbro.registerLanguage(frFR as numbro.NumbroLanguage);
numbro.registerLanguage(nlNL as numbro.NumbroLanguage);

const SerpentineFormatRegex = /{(.?:)?([^{}]*)}/g;
const OperationRegex = /\(([x*/])(\d+)\)$/;
const NumberRegex = /[,0#]*\.[0#]*/g;
const OperationMap = {
    x: (a: number, b: number) => a * b,
    "*": (a: number, b: number) => a * b,
    "/": (a: number, b: number) => a / b,
};

export type Operation = keyof typeof OperationMap;

export const formatter: FormatFn<RawValue> = (meta, rawValue, rawObj) => {
    if (!(Array.isArray(rawValue) ? rawValue.length : rawValue)) {
        return meta.null_replacement;
    }
    switch (meta.type) {
        case "string":
            return formatString(meta, rawValue as string, rawObj);
        case "date":
            return formatDate(meta, rawValue as string, rawObj);
        case "number":
            return formatNumber(meta, rawValue as string, rawObj);
        case "currency":
            return formatCurrency(meta, rawValue as string, rawObj);
        case "document":
            return formatDocument(meta, rawValue as RawDocumentValue[], rawObj);
        default:
            return JSON.stringify(rawValue);
    }
};

const formatString: FormatFn = ({ format }, str, _) => {
    return format ? format.replace(SerpentineFormatRegex, () => str) : str;
};

const formatDate: FormatFn = ({ format }, date, _) => {
    const defaultFormat = "YYYY-MM-DD";
    const parseFormat = defaultFormat;
    const parsedDate = dayjs(date, parseFormat);

    if (!parsedDate.isValid()) {
        return date;
    }
    if (!format) {
        return parsedDate.format(defaultFormat);
    }

    return format.replace(
        SerpentineFormatRegex,
        (fullMatch, firstGroup, secondGroup) => {
            return parsedDate.format(secondGroup || defaultFormat);
        },
    );
};

function getPattern(formatString: string) {
    const match = formatString.match(NumberRegex);
    if (!match) {
        throw new Error(`Invalid number pattern ${formatString}`);
    }
    return match[0];
}

function formatNumbroWithLDML(
    numbroNumber: numbro.Numbro,
    formatString: string,
) {
    const semicolon = formatString.indexOf(";");
    let formattedNumber = "";
    if (semicolon > -1) {
        const posFormatString = formatString.slice(0, semicolon);
        const posPattern = getPattern(posFormatString);
        const negFormatString = formatString.slice(
            semicolon + 1,
            formatString.length,
        );
        const negPattern = getPattern(negFormatString);
        formattedNumber = numbroNumber.format(posPattern);

        if (numbroNumber.value() >= 0) {
            return posFormatString.replace(posPattern, formattedNumber);
        }
        // formattedNumber needs to be stripped of its - sign
        formattedNumber = formattedNumber.slice(1, formattedNumber.length);
        return negFormatString.replace(negPattern, formattedNumber);
    }

    const pattern = getPattern(formatString);
    formattedNumber = numbroNumber.format(pattern);

    return formatString.replace(pattern, formattedNumber);
}

function getParsedNumber(num: string) {
    // Special case
    const allowedStrings = ["N/A"];
    if (allowedStrings.indexOf(num.toUpperCase()) !== -1) {
        return "n/a";
    }

    // Validation
    const parsedNumber = parseFloat(num);
    if (isNaN(parsedNumber)) return "";

    return parsedNumber;
}

const formatNumber: FormatFn = ({ format }, num, _) => {
    let parsedNumber = getParsedNumber(num);
    if (!format || typeof parsedNumber !== "number") return num;

    // Find operation strings in ret and apply them to the num
    const operationFormatMatch = format.match(OperationRegex);
    if (operationFormatMatch) {
        const operation = OperationMap[operationFormatMatch[1] as Operation];
        const numberMatch = parseFloat(operationFormatMatch[2] as string);
        parsedNumber = operation(parsedNumber, numberMatch);
    }
    const numbroNumber = numbro(parsedNumber);

    // Remove any operationFormatMatch from formatString and replace numeralFormatMatches with the formatted number
    return format
        .split(OperationRegex)[0]!
        .replace(
            SerpentineFormatRegex,
            (fullMatch, firstGroup, secondGroup) => {
                return formatNumbroWithLDML(numbroNumber, secondGroup);
            },
        );
};

const formatCurrency: FormatFn = (meta, num, rawObj) => {
    const parsedNumber = getParsedNumber(num);
    const { format_rules: formatRules, format: defaultFormatString } = meta;
    const currency = meta.currency ?? rawObj?.currency;

    if (typeof parsedNumber !== "number") {
        return num;
    } else if (!currency || !formatRules || !Object.keys(formatRules).length) {
        return formatNumber(meta, parsedNumber.toString(), rawObj);
    }

    const format = formatRules
        .filter((rule) => Object.keys(rule.formats).indexOf(currency) > -1)
        .sort((a, b) => parseFloat(b.min_value) - parseFloat(a.min_value))[0];

    return format && currency
        ? formatNumber(
              { ...meta, format: format.formats[currency as Currency] ?? null },
              parsedNumber.toString(),
              rawObj,
          )
        : formatNumber(
              { ...meta, format: defaultFormatString },
              parsedNumber.toString(),
              rawObj,
          );
};

const formatDocument: FormatFn<RawDocumentValue[]> = (_, documents) => {
    return documents.map(([url, name, lang, asAtDate]) => ({
        url,
        name,
        lang,
        asAtDate,
    }));
};

export const abbrNumber = (numStr: string) => {
    const num = getParsedNumber(numStr);
    if (typeof num !== "number") {
        return num;
    }
    const absNum = Math.abs(num);
    if (absNum >= 1e9) {
        return (num / 1e9).toFixed(2) + "B";
    } else if (absNum >= 1e6) {
        return (num / 1e6).toFixed(2) + "M";
    } else if (absNum >= 1e3) {
        return (num / 1e3).toFixed(2) + "K";
    } else {
        return num.toString();
    }
};

export const localeFormat = (
    type: "date" | "number",
    locale: string,
    val: string,
) => {
    switch (type) {
        case "date":
            return val;
        case "number":
            return localeFormatNumber(locale, val);
    }
};

function localeFormatNumber(locale: string, val: string) {
    if (!/^it|de|es|fr|nl/gi.test(locale)) {
        return val;
    }
    const _val = val.replaceAll(",", "");
    const hasDecimal = val.includes(".");
    const numbroLocale = locale.replace("_", "-"); // en_GB -> en-GB
    const numRegex = /(?<num>\d+(\.\d+)?)/g;
    const mantissa = /.*\.(?<mantissa>\d+)\D*$/g.exec(val)?.groups?.mantissa;
    // const numRegex = /(?<num>[\d.]+)/;

    const num = numRegex.exec(_val)?.groups?.num;
    if (!num) {
        return val;
    }
    const parsedNum = parseFloat(num);
    numbro.setLanguage(numbroLocale);
    const formatted = _val.replaceAll(
        numRegex,
        numbro(parsedNum).format({
            thousandSeparated: true,
            mantissa: hasDecimal ? (mantissa ? mantissa.length : 2) : 0,
        }),
    );
    numbro.setLanguage("en-GB");
    return formatted;
}

export const parseFloatIntl = (x: string) =>
    parseFloat(/,/.test(x) ? x.replace(",", ".") : x);
